import { Flex, ModuleLayout, Space, Spacing } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { matchPath, Navigate, useLocation } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { SalesReportContextProvider } from './contexts/salesReportContext'
import { SalesReportNavigationTabs } from './elements/SalesReportNavigationTabs'
import { SalesReportSideActions } from './elements/SalesReportSideActions'
import { SalesReportToolbar } from './elements/SalesReportToolbar'
import { useIsSalesReportDataEmpty } from './hooks/useIsSalesReportDataEmpty'
import { useSalesReportRoutes } from './hooks/useSalesReportRoutes'
import { salesReportVariantRoutes } from './utils/salesReportVariantRoutes'

export const SalesReport = (): ReactElement | null => {
  const { organization } = useUserOrganization()
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const isSalesReportDataEmpty = useIsSalesReportDataEmpty()
  const { fallbackSalesReportUrl, salesReportEmptyUrl, salesReportMainUrl } = useSalesReportRoutes()

  if (!organization || !salesReportEmptyUrl) {
    return null
  }

  if (isSalesReportDataEmpty) {
    return <Navigate to={salesReportEmptyUrl} replace />
  }

  // Bare main URL has no variant of its own - send it to the fallback/default variant. This must
  // stay an early return: SalesReportSideActions and SalesReportToolbar (below) consume useSalesReportFilters, whose
  // seeded query params would race a sibling Redirect and bounce the URL back.
  if (matchPath(salesReportMainUrl, pathname)) {
    return <Navigate to={fallbackSalesReportUrl} replace />
  }

  const currentVariantRoute = salesReportVariantRoutes.find(({ getVariantRoute }) =>
    matchPath(getVariantRoute(organization.url), pathname),
  )

  // Defensive: SalesReportRoute (the caller) already guarantees pathname matches one of the
  // known paths above, but the two match systems could be transiently out of sync mid-navigation.
  if (!currentVariantRoute) {
    return null
  }

  const { component: Component } = currentVariantRoute

  return (
    <SalesReportContextProvider>
      <ModuleLayout
        title={t('sales_report')}
        headerBottomActions={
          <Flex flexDirection="column" gap={Spacing.S8}>
            <Space size="s2" />
            <SalesReportNavigationTabs />
          </Flex>
        }
        sideActions={<SalesReportSideActions />}
      >
        <Flex flexDirection="column" gap={Spacing.S16} width="100%">
          <SalesReportToolbar />
          <Component />
        </Flex>
      </ModuleLayout>
    </SalesReportContextProvider>
  )
}
